import { FiInfo } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';

import { UiPen } from '@vyaguta/vyaguta-icons/ui';

import LFModal from 'components/common/LFModal';

import PopOver from 'components/common/popover';
import InfoBox from 'components/common/infoBox';
import { SkillCategoryChild } from 'components/common/skills';
import type { OverlaySkill } from 'components/common/skills/types';
import MyTeam from '../myTeam/MyTeam';

import * as skillService from 'services/skillService';

import { intersect } from 'utils/array';
import { isObjectEmpty } from 'utils/object';
import { getFormattedDate } from 'utils/date';
import { handleError } from 'utils/errorHandler';
import { toLinkComponent, toTextComponent } from 'utils/component';
import { getEndSkillIds, getSearchTreeWithRating } from 'utils/skillV2';
import { formatEmployeeAvailabilityTime, getFullName } from 'utils/user';
import { extractFullName, interpolate, pluralize, startCase, underscoreToSpace } from 'utils/string';

import * as routes from 'constants/routes';
import { SKILL_STATUS } from 'constants/skills';
import { EMPLOYEES, USER_PERMISSIONS, ATTENDANCE_PERMISSION } from 'constants/permissions';
import { DATE_FORMAT_FOR_DISPLAY, MONTH_DAY_DISPLAY_FORMAT, ROLE } from 'constants/appConstants';

import useNonInitialEffect from 'hooks/useNonInitialEffect';
import { useMyRoleQuery } from 'hooks/query/useMyRole';
import { useUserPermissions } from 'hooks/query/useUserInfo';

import FlaggedSkill from './FlaggedSkill';
import VerifiedSkill from './VerifiedSkill';
import SkillReviewOverlay from '../skills/components/SkillReviewOverlay';
import ProjectAllocations from './ProjectAllocations';
import EmployeePageDetail from '../../components/EmployeePageDetail';
import EmployeeHistoryList from '../../components/EmploymentHistoryList';
import { EmployeeRosterModal } from 'components/common/profile/EmployeeRoster/EmployeeRosterModal';
import { EmployeeRosterButton } from 'components/common/profile/EmployeeRoster/EmployeeRosterButton';
import { SkillWithStatus, SkillNode } from 'interface/skillTree';
import { Rating } from 'interface/skill';
import { EmployeeLike } from 'interface/userUtils';
import { useGlobalRatingsQuery } from 'hooks/query/useSkillQueries';

interface ProfileDescriptionProps {
  authUser: { id: number };
  employee: EmployeeLike & {
    id: number;
    leaveIssuer?: { id: number; firstName: string; middleName?: string; lastName: string };
    teamManager?: { id: number; firstName: string; middleName?: string; lastName: string };
    department?: { name?: string };
    workingShift?: string;
    availabilityTime?: { startTime: string; startTimeType: string; endTime: string; endTimeType: string };
    scheduledType?: string;
    pastExperience?: number;
    resignation?: { id?: number; tentativeExitDate?: string; reason?: string };
  };
}

interface ProfileEmployeeData {
  id: number;
  leaveIssuer?: { id: number; firstName: string; middleName?: string; lastName: string };
  teamManager?: { id: number; firstName: string; middleName?: string; lastName: string };
  department?: { name?: string };
  joinDate?: string;
  workingShift?: string;
  availabilityTime?: { startTime: string; startTimeType: string; endTime: string; endTimeType: string };
  availabilityTimeRange?: { startTime: string; startTimeType: string; endTime: string; endTimeType: string };
  scheduledType?: string;
  pastExperience?: number;
  resignation?: { id?: number; tentativeExitDate?: string; reason?: string };
  gender?: string;
  dateofBirth?: string;
  birthday?: string;
  bloodGroup?: string;
  temporaryAddress?: string;
  permanentAddress?: string;
  country?: string;
  timezone?: string;
  empStatus?: string;
}

const ProfileDescription: React.FC<ProfileDescriptionProps> = (props) => {
  const { authUser, employee } = props;
  const employeeData = employee as ProfileEmployeeData;
  const EmployeePageDetailLoose = EmployeePageDetail as React.FC<{
    id: number | string;
    employee: EmployeeLike;
    fieldMap: Array<{
      label: string;
      DataComponent?: React.ComponentType<object> | null;
      props?: object;
      isUnAuthorizedUser?: boolean;
    }>;
  }>;

  const permissions = useUserPermissions();

  const [skillCatalog, setSkillCatalog] = useState<SkillNode[]>([]);
  const [userSkills, setUserSkills] = useState<SkillWithStatus[]>((employee?.skills as SkillWithStatus[]) || []);
  const [refreshUserSkills, setRefreshUserSkills] = useState(false);

  const [isRosterModalOpen, setIsRosterModalOpen] = useState(false);

  const [appraiserIds, setAppraiserIds] = useState<Array<number | string>>([]);

  const isSelf = authUser.id === employee.id;
  const isLeaveIssuer = employeeData.leaveIssuer?.id === authUser.id;

  const isTeamManager = authUser.id === employeeData.teamManager?.id;

  const canViewEmployeeProfileRoles = isSelf || permissions[USER_PERMISSIONS.VIEW];
  const {
    data: userRoles = [],
    isError: isUserRolesError,
    error: userRolesError,
    errorUpdatedAt: userRolesErrorUpdatedAt,
  } = useMyRoleQuery(employee.id, {
    enabled: Boolean(!isObjectEmpty(employee) && canViewEmployeeProfileRoles),
  });
  const lastHandledUserRolesErrorAtRef = useRef(0);

  const isAppraiser = useMemo(() => {
    return appraiserIds.some((appraiserId) => appraiserId === authUser.id);
  }, [appraiserIds, authUser]);

  useEffect(() => {
    if (!isUserRolesError || !userRolesError || userRolesErrorUpdatedAt <= lastHandledUserRolesErrorAtRef.current) {
      return;
    }

    handleError(userRolesError);
    lastHandledUserRolesErrorAtRef.current = userRolesErrorUpdatedAt;
  }, [isUserRolesError, userRolesError, userRolesErrorUpdatedAt]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = (await skillService.fetchAll({})) as { data: SkillNode[] };

        setSkillCatalog(Array.isArray(data) ? data : []);
      } catch (error) {
        handleError(error);
      }
    })();
  }, []);

  useNonInitialEffect(() => {
    (async () => {
      if (!employee.id) {
        return;
      }

      try {
        const { data } = (await skillService.fetchByUserId(employee.id)) as { data: SkillWithStatus[] };

        setUserSkills(data);
      } catch (error) {
        handleError(error);
      }
    })();
  }, [employee, refreshUserSkills]);

  interface TentativeExitProps {
    resignation?: { tentativeExitDate?: string; reason?: string };
  }

  const TentaiveExit: React.FC<TentativeExitProps> = ({ resignation }) => {
    return (
      <>
        <span className="pr-5">
          {resignation?.tentativeExitDate
            ? getFormattedDate(resignation.tentativeExitDate, DATE_FORMAT_FOR_DISPLAY)
            : '-'}
        </span>
        <PopOver
          interactive
          html={
            <div className="tooltip-info">
              <p className="tooltip-info__title">{resignation?.reason}</p>
            </div>
          }
        >
          <FiInfo color="#2196F3" size={12} />
        </PopOver>
      </>
    );
  };

  const getEmployeeRoles = (roles: string[]) => {
    return roles.join(', ');
  };

  const pastExperience = useMemo(() => {
    if (!employeeData.pastExperience) {
      return null;
    }

    const months = employeeData.pastExperience % 12;
    const years = Math.floor(employeeData.pastExperience / 12);

    return (
      <span className="reset-text">{`${years} ${pluralize('year', years)} ${months} ${pluralize(
        'month',
        months
      )}`}</span>
    );
  }, [employeeData.pastExperience]);

  return (
    <>
      <div className="container d-flex fd-row flex-fix name-wrap">
        <div className="width--full">
          <div className="full-scope-card emp-info-container mt-25">
            <div className="emp-info-title">Work Info</div>
            <EmployeePageDetailLoose
              id={employee.id}
              employee={employee}
              fieldMap={
                [
                  {
                    label: 'Leave Issuer',
                    DataComponent: toLinkComponent(
                      employeeData.leaveIssuer && extractFullName(employeeData.leaveIssuer),
                      routes.EMPLOYEE_PROFILE,
                      {
                        id: employeeData.leaveIssuer && employeeData.leaveIssuer.id,
                      },
                      true
                    ),
                  },
                  {
                    label: 'Team Manager',
                    DataComponent: toLinkComponent(
                      employeeData.teamManager && extractFullName(employeeData.teamManager),
                      routes.EMPLOYEE_PROFILE,
                      {
                        id: employeeData.teamManager && employeeData.teamManager.id,
                      },
                      true
                    ),
                  },
                  {
                    label: 'Department',
                    DataComponent: toTextComponent(employeeData.department?.name),
                  },
                  {
                    label: 'Employee since',
                    DataComponent: toTextComponent(getFormattedDate(employeeData.joinDate, DATE_FORMAT_FOR_DISPLAY)),
                  },
                  {
                    label: 'Working Shift',
                    DataComponent: WorkingShift,
                    props: {
                      permissions,
                      label: 'View attendance',
                      workingShift: employeeData.workingShift,
                      employeeId: employee.id,
                    },
                  },
                  {
                    label: 'Availability Time',
                    DataComponent: AvailabilityTime,
                    props: {
                      authUserId: authUser.id,
                      permissions,
                      label: 'View detail roster',
                      availabilityTime: employeeData.availabilityTime || employeeData.availabilityTimeRange,
                      isRosterModalOpen,
                      setIsRosterModalOpen,
                      employee,
                      isSelf,
                    },
                  },
                  {
                    label: 'Working Type',
                    DataComponent: toTextComponent(underscoreToSpace(employeeData.scheduledType)),
                  },
                  ...(pastExperience
                    ? [
                        {
                          label: 'Previous Experience',
                          DataComponent: (() => <>{pastExperience}</>) as React.ComponentType,
                          isUnAuthorizedUser:
                            !isSelf && !isTeamManager && !isAppraiser && !permissions[EMPLOYEES.PROFILE.UPDATE],
                        },
                      ]
                    : []),
                  {
                    label: 'Roles',
                    DataComponent: toTextComponent(getEmployeeRoles(userRoles)),
                    isUnAuthorizedUser: !isSelf && !permissions[USER_PERMISSIONS.VIEW],
                  },
                  {
                    label: 'Tentative Exit Date',
                    DataComponent: employeeData?.resignation?.id ? TentaiveExit : null,
                    isUnAuthorizedUser: !isSelf && !isLeaveIssuer && !permissions[EMPLOYEES.RESIGNATIONS.VIEW],
                    props: { resignation: employeeData?.resignation },
                  },
                  {
                    label: 'Skill set',
                    DataComponent: EmployeeSkills,
                    props: { skills: userSkills, totalSkills: skillCatalog, employee, authUser, setRefreshUserSkills },
                  },
                ] as never[]
              }
            />
          </div>

          <div className="full-scope-card emp-info-container mt-25">
            <div className="emp-info-title">Personal Info</div>
            <EmployeePageDetailLoose
              id={employee.id}
              employee={employee}
              fieldMap={
                [
                  {
                    label: 'Gender',
                    DataComponent: toTextComponent(startCase(employeeData.gender)),
                  },
                  {
                    label: 'Birthday',
                    DataComponent: toTextComponent(
                      authUser.id === employee.id
                        ? getFormattedDate(employeeData.dateofBirth, MONTH_DAY_DISPLAY_FORMAT)
                        : employeeData.birthday
                    ),
                  },
                  {
                    label: 'Blood Group',
                    DataComponent: toTextComponent(employeeData.bloodGroup),
                  },
                  {
                    label: 'Temporary Address',
                    DataComponent: toTextComponent(employeeData.temporaryAddress),
                  },
                  {
                    label: 'Permanent Address',
                    DataComponent: toTextComponent(employeeData.permanentAddress),
                  },
                  {
                    label: 'Country',
                    DataComponent: toTextComponent(employeeData.country),
                  },
                  {
                    label: 'Time Zone',
                    DataComponent: toTextComponent(employeeData.timezone),
                  },
                ] as never[]
              }
            />
          </div>

          <ProjectAllocations
            employeeName={extractFullName(employee as { firstName: string; middleName?: string; lastName: string })}
            employeeId={employee.id}
            authUserId={authUser.id}
            employee={employee}
            isSelf={isSelf}
            setAppraiserIds={setAppraiserIds}
          />
        </div>

        <div className="profile-right">
          <div
            className={classNames(
              'full-scope-card',
              'emp-history-area',
              'emp-history-area--profile',
              'mt-25',
              'moml-30',
              'padding--rl',
              'custom-scroll-bar'
            )}
          >
            <EmployeeHistoryList employee={employee as never} />
          </div>
          <MyTeam employee={employee as EmployeeLike & { id: number; firstName: string }} isSelf={isSelf} />
        </div>
      </div>
    </>
  );
};

interface WorkingShiftProps {
  permissions: Record<string, boolean>;
  workingShift?: string;
  label: string;
  employeeId?: number | string;
}

const WorkingShift: React.FC<WorkingShiftProps> = ({ permissions, workingShift, label, employeeId }) => {
  const workingShiftTextRenderer = toTextComponent(underscoreToSpace(workingShift)) as () => React.ReactNode;
  const workingShiftText = workingShiftTextRenderer();

  return (
    <div className="emp-profile-info">
      <span>{workingShiftText}</span>
      {!!permissions[ATTENDANCE_PERMISSION.VIEW] && (
        <span className="reset-text">
          {employeeId &&
            (
              toLinkComponent(
                label,
                interpolate(routes.USER_ATTENDANCE, { userId: employeeId }),
                {},
                false
              ) as () => React.ReactNode
            )()}
        </span>
      )}
    </div>
  );
};

interface AvailabilityTimeProps {
  authUserId: number;
  availabilityTime?: { startTime: string; startTimeType: string; endTime: string; endTimeType: string };
  isRosterModalOpen: boolean;
  setIsRosterModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  employee: EmployeeLike;
  isSelf: boolean;
}

const AvailabilityTime: React.FC<AvailabilityTimeProps> = ({
  authUserId,
  availabilityTime,
  isRosterModalOpen,
  setIsRosterModalOpen,
  employee,
  isSelf,
}) => {
  const { data: userRole = [] } = useMyRoleQuery(authUserId);

  const ROSTER_VIEW_ROLES = [
    ROLE.SUPER_ADMIN,
    ROLE.HR_ADMIN,
    ROLE.PROJECTS_ADMIN,
    ROLE.SPEARHEAD,
    ROLE.PROJECT_MANAGER,
    ROLE.ACCOUNT_MANAGER,
    ROLE.TEAM_MANAGER,
  ];

  const hasRosterViewAccess = Boolean(intersect(userRole, ROSTER_VIEW_ROLES).length);

  return (
    <div className="emp-profile-info">
      <span>
        {(
          toTextComponent(
            formatEmployeeAvailabilityTime(
              availabilityTime || {
                startTime: '',
                startTimeType: '',
                endTime: '',
                endTimeType: '',
              }
            )
          ) as () => React.ReactNode
        )()}
      </span>
      {(hasRosterViewAccess || isSelf) && (
        <span className="reset-text">
          <EmployeeRosterModal
            TriggerBtn={() => <EmployeeRosterButton setIsRosterModalOpen={setIsRosterModalOpen} />}
            isOpen={isRosterModalOpen}
            onClose={() => {
              setIsRosterModalOpen(false);
            }}
            employee={employee}
          />
        </span>
      )}
    </div>
  );
};

interface EmployeeSkillsProps {
  skills: SkillWithStatus[];
  totalSkills: SkillNode[];
  employee: EmployeeLike;
  authUser: EmployeeLike & { id: number };
  setRefreshUserSkills: React.Dispatch<React.SetStateAction<boolean>>;
}

const EmployeeSkills: React.FC<EmployeeSkillsProps> = ({
  skills,
  totalSkills,
  employee,
  authUser,
  setRefreshUserSkills,
}) => {
  const history = useHistory();

  const permissions = useUserPermissions();
  const {
    data: globalRatings = [],
    isError: isGlobalRatingsError,
    error: globalRatingsError,
    errorUpdatedAt: globalRatingsErrorUpdatedAt,
  } = useGlobalRatingsQuery();
  const lastHandledGlobalRatingsErrorAtRef = useRef(0);

  const [isOverlayOpenFor, setIsOverlayOpenFor] = useState<OverlaySkill<SkillNode> | null>(null);
  const [skillModalOpen, setSkillModalOpen] = useState(false);
  const globalRatingScale = useMemo<Rating[]>(
    () =>
      globalRatings.map((item: number | Rating) =>
        typeof item === 'number' ? { rating: item, description: '' } : item
      ),
    [globalRatings]
  );

  useEffect(() => {
    if (
      !isGlobalRatingsError ||
      !globalRatingsError ||
      globalRatingsErrorUpdatedAt <= lastHandledGlobalRatingsErrorAtRef.current
    ) {
      return;
    }

    handleError(globalRatingsError);
    lastHandledGlobalRatingsErrorAtRef.current = globalRatingsErrorUpdatedAt;
  }, [isGlobalRatingsError, globalRatingsError, globalRatingsErrorUpdatedAt]);

  const isSelf = authUser.id === employee.id;

  const endSkillIds = useMemo(() => getEndSkillIds(totalSkills), [totalSkills]);

  const endSkills = useMemo(() => skills.filter((skill) => endSkillIds.has(skill.id)), [skills, endSkillIds]);

  const nonVerifiedSkillsCount = endSkills.filter((curr) => !curr.skillStatus?.status).length;

  const verifiedSkillsCount = endSkills.filter((curr) => curr.skillStatus?.status === SKILL_STATUS.APPROVED).length;

  const flaggedSkillsCount = endSkills.filter(
    (curr) => curr.skillStatus?.status === SKILL_STATUS.FLAGGED && curr.skillStatus?.rating !== 0
  ).length;

  const deletionRecommendationCount = endSkills.filter((curr) => curr.skillStatus?.rating === 0).length;

  const filteredSelectedSkills = useMemo(
    () => getSearchTreeWithRating(endSkills, totalSkills),
    [endSkills, totalSkills]
  );

  const isTeamManager = authUser.id === (employee as ProfileEmployeeData).teamManager?.id;

  const hasOverlayPermission = isTeamManager || (!!isOverlayOpenFor?.skillStatus && isSelf);

  const employeeFullName = getFullName(employee);

  const skillCount = endSkills.length;

  return (
    <div className="user-skillsv2-profile">
      <div className="user-skillsv2-profile__container">
        <div className="d-flex fd-row f-wrap align-items-center">
          <div>{skillCount} skills</div>
          {!!skillCount && (
            <div onClick={() => setSkillModalOpen(true)} className="view-link">
              View all
            </div>
          )}
          {!!nonVerifiedSkillsCount && isTeamManager && <InfoBox message="Verify the skill sets" />}
        </div>

        {!!skillCount && (
          <div className="skill-count-container">
            <VerifiedSkill count={{ verifiedSkills: verifiedSkillsCount, totalSkills: endSkills.length }} />
            {!!flaggedSkillsCount && <FlaggedSkill count={flaggedSkillsCount} type="flag" />}
            {!!deletionRecommendationCount && <FlaggedSkill count={deletionRecommendationCount} type="delete" />}
          </div>
        )}
      </div>

      <LFModal
        isOpen={skillModalOpen}
        onClose={() => setSkillModalOpen(false)}
        title={{ text: `Selected Skills of ${employeeFullName}`, type: 'info' }}
        titlePrefixHtml={
          isSelf || permissions[EMPLOYEES.SKILLS.UPDATE] ? (
            <button
              className="profile-skills-edit"
              onClick={() => {
                history.push(interpolate(routes.UPDATE_EMPLOYEE_SKILLS, { id: employee?.id }));
              }}
            >
              <UiPen />
            </button>
          ) : (
            ''
          )
        }
        htmlContent={
          <div className="skill-set-area__container profile-skillset">
            <div className="mb-15">
              {!!flaggedSkillsCount && isSelf && (
                <InfoBox message="Please check all the flagged skills by clicking on them." />
              )}

              {!!nonVerifiedSkillsCount && isTeamManager && (
                <InfoBox message="Verify the skill ratings by flagging the ones that do not match." />
              )}
            </div>

            <div className="skills-list-container">
              {filteredSelectedSkills.map((skill) => {
                return (
                  <SkillCategoryChild
                    key={skill.id}
                    selectedCategory={skill}
                    setIsOverlayOpenFor={setIsOverlayOpenFor}
                    isOpenFor={hasOverlayPermission ? isOverlayOpenFor : null}
                    isSelf={isSelf}
                    isTeamManager={isTeamManager}
                    openEditCategoryModal={() => undefined}
                    openAddSkillModal={() => undefined}
                    openEditSkillModal={() => undefined}
                  />
                );
              })}
            </div>
            {!!isOverlayOpenFor && (
              <SkillReviewOverlay
                isOpenFor={hasOverlayPermission && isOverlayOpenFor ? isOverlayOpenFor : null}
                setIsOverlayOpenFor={setIsOverlayOpenFor}
                globalRatingScale={globalRatingScale}
                employee={employee as never}
                userSkills={skills}
                setRefreshUserSkills={setRefreshUserSkills}
              />
            )}
          </div>
        }
        renderFooter={false}
        closeButton={true}
        className="profile-skill-modal"
      />
    </div>
  );
};

export default ProfileDescription;
